import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { createPayload, updatePayload, type PayloadSpec } from '../../../lib/forms/payload.js';
import { pmsCredentials } from './endpoints.js';
import {
  type CreatedPmsCredential,
  type PmsCredentialFormInput,
  createdPmsCredential,
} from './schemas.js';

const WRITTEN_KEYS = pmsCredentials.writtenKeys;

const PMS_CREDENTIAL_PAYLOAD: PayloadSpec = {
  required: [
    'name',
    'pmsProvider',
    'travelAgentId',
    'marketSegmentId',
    'bookingSourceId',
    'partialReservationExpireTime',
    'agentId',
    'agentSecret',
    'clientId',
    'clientSecret',
    'baseUrl',
    'timezone',
  ],
  optional: [
    'rateGridAgentId',
    'masterRateTravelAgentId',
    'reservationTravelAgentId',
    'useTestEnvironment',
    'status',
    'moduleType',
  ],
  numeric: [
    'travelAgentId',
    'marketSegmentId',
    'bookingSourceId',
    'partialReservationExpireTime',
    'agentId',
    'clientId',
    'rateGridAgentId',
    'masterRateTravelAgentId',
    'reservationTravelAgentId',
  ],
};

function normaliseModuleType(value: string): readonly string[] {
  const trimmed = value.trim();
  if (trimmed === '') return [];
  return trimmed
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function createPmsCredentialPayload(
  values: PmsCredentialFormInput,
): Record<string, unknown> {
  const payload = createPayload(values, PMS_CREDENTIAL_PAYLOAD);

  payload.useTestEnvironment = values.useTestEnvironment === 'true';
  payload.moduleType =
    values.moduleType === '' ? ['distribution'] : normaliseModuleType(values.moduleType);

  return payload;
}

export function updatePmsCredentialPayload(
  values: PmsCredentialFormInput,
  version: number,
): Record<string, unknown> {
  const payload = updatePayload(values, PMS_CREDENTIAL_PAYLOAD, version);

  payload.useTestEnvironment = values.useTestEnvironment === 'true';
  payload.moduleType = normaliseModuleType(values.moduleType);

  return payload;
}

export function useCreatePmsCredential(): UseMutationResult<
  CreatedPmsCredential,
  unknown,
  PmsCredentialFormInput
> {
  return useApiMutation<PmsCredentialFormInput, CreatedPmsCredential>({
    mutationFn: async (api, input) => {
      const payload = createPmsCredentialPayload(input);
      const answer = await api.post<unknown>(
        pmsCredentials.endpoints.create,
        payload,
        crypto.randomUUID(),
      );
      return parseOrThrow(createdPmsCredential, answer, pmsCredentials.endpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdatePmsCredentialVariables {
  readonly id: string;
  readonly version: number;
  readonly values: PmsCredentialFormInput;
}

export function useUpdatePmsCredential(): UseMutationResult<
  unknown,
  unknown,
  UpdatePmsCredentialVariables
> {
  return useApiMutation<UpdatePmsCredentialVariables, unknown>({
    mutationFn: async (api, { id, version, values }) => {
      await api.patch<unknown>(
        pmsCredentials.endpoints.update(id),
        updatePmsCredentialPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeletePmsCredential(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, id) => {
      await api.delete<unknown>(pmsCredentials.endpoints.delete(id));
    },
    invalidate: WRITTEN_KEYS,
  });
}
